import moment, { Moment } from 'moment/moment'

import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignPlanCampaignBuilder, MasterCampaignStub } from 'api/interfaces/MasterCampaignStub'

import { Campaign, IntervalUnit } from '../api/interfaces'

export function calculateTotal(intervalBudget?: number, intervalDuration?: number): number {
  if (!intervalBudget || !intervalDuration) {
    return 0
  }

  return intervalBudget * intervalDuration
}

export const getMasterCampaignStubAverageMonthlyBudgetOld = (masterCampaignPlan: MasterCampaignPlan, masterCampaignStub: MasterCampaignStub) => {
  return getMasterCampaignBuildersAverageMonthlyBudget(masterCampaignPlan.aiUsedDurationInMonths, masterCampaignStub.campaignBuilders)
}

export const getMasterCampaignStubAverageMonthlyBudget = (masterCampaignStub: MasterCampaignStub) => {
  const durationMonths = getTotalMonthRange(masterCampaignStub.campaignBuilders)
  return getMasterCampaignBuildersAverageMonthlyBudget(durationMonths, masterCampaignStub.campaignBuilders)
}

export const getMasterCampaignBuildersAverageMonthlyBudget = (durationInMonths: number, builders?: MasterCampaignPlanCampaignBuilder[]) => {
  if (!builders || builders.length === 0) {
    return 0
  }

  const totalBudget = builders.reduce((total, builder) => {
    return total + calculateTotal(builder.intervalBudget, builder.intervalDuration)
  }, 0)

  return totalBudget / durationInMonths
}

export const getMasterCampaignStubStartDate = (masterCampaignStub?: MasterCampaignStub): Moment | undefined => {
  const builders = [...(masterCampaignStub?.campaignBuilders ?? [])]
  if (!builders || builders.length === 0) {
    return undefined
  }

  const earliestCampaign = builders.sort((a, b) => {
    return moment(a.startDate).isBefore(moment(b.startDate)) ? -1 : 1
  })[0]
  if (!earliestCampaign || !earliestCampaign.startDate) {
    return undefined
  }

  return moment(earliestCampaign.startDate)
}

export const getEarliestStartDate = (campaigns: Campaign[] | MasterCampaignPlanCampaignBuilder[]): Moment | undefined => {
  if (!campaigns || campaigns.length === 0) {
    return undefined
  }

  const orderedCampaigns = [...campaigns].sort((a, b) => {
    return moment(a.startDate).isBefore(moment(b.startDate)) ? -1 : 1
  })

  const earliestCampaign = orderedCampaigns[0]

  if (!earliestCampaign || !earliestCampaign.startDate) {
    return undefined
  }

  return moment(earliestCampaign.startDate)
}

export const getLatestEndDate = (campaigns: Campaign[] | MasterCampaignPlanCampaignBuilder[]) => {
  if (!campaigns || campaigns.length === 0) {
    return undefined
  }

  const orderedCampaigns = [...campaigns].sort((a, b) => {
    return getEndDate(a)?.isAfter(getEndDate(b)) ? -1 : 1
  })
  const latestCampaign = orderedCampaigns[0]

  if (!latestCampaign) {
    return undefined
  }

  return getEndDate(latestCampaign)
}

export const getEndDate = (item: Campaign | MasterCampaignPlanCampaignBuilder): Moment | undefined => {
  if (!item || !item.startDate) {
    return undefined
  }

  let intervalUnit: IntervalUnit
  let intervalFrequency: number
  let totalIntervals: number

  const campaign = item as Campaign
  const builder = item as MasterCampaignPlanCampaignBuilder

  if (builder.intervalUnit && builder.intervalFrequency && builder.intervalDuration) {
    intervalUnit = builder.intervalUnit
    intervalFrequency = builder.intervalFrequency
    totalIntervals = builder.intervalDuration
  } else if (campaign.flightSchedule) {
    intervalUnit = campaign.flightSchedule.intervalUnit
    intervalFrequency = campaign.flightSchedule.intervalUnitFrequency
    totalIntervals = campaign.flightSchedule.totalIntervals
  } else {
    return undefined
  }

  if (campaign.endDate) {
    return moment(campaign.endDate)
  }

  let momentUnit = 'month'
  if (intervalUnit === IntervalUnit.Day) {
    momentUnit = 'day'
  } else if (intervalUnit === IntervalUnit.Week) {
    momentUnit = 'week'
  } else if (intervalUnit === IntervalUnit.Month) {
    momentUnit = 'month'
  }

  return moment(campaign.startDate).add(momentUnit as moment.unitOfTime.DurationConstructor, totalIntervals * intervalFrequency)
}

export const getTotalMonthRange = (campaigns: Campaign[] | MasterCampaignPlanCampaignBuilder[]): number => {
  if (!campaigns || campaigns.length === 0) {
    return 0
  }

  const startDate = getEarliestStartDate(campaigns)
  const endDate = getLatestEndDate(campaigns)
  if (!startDate || !endDate) {
    return 0
  }
  return endDate.endOf('month').diff(startDate.endOf('month'), 'months')
}
